import { useEffect, useMemo, useState } from "react";
import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { TypeBadge, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, User, MapPin, Tag, History, Pencil, Trash2, Plus, Save, Users, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import PortalAccessPanel from "../components/PortalAccessPanel";

const fmtEUR = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("stammdaten");
  const [cust, setCust] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState(null);
  const [prices, setPrices] = useState([]);
  const [b2bMap, setB2bMap] = useState({});
  const [items, setItems] = useState([]);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const r = await api.get(`/customers/${id}`);
      setCust(r.data);
      setForm({ ...r.data, contact_persons: r.data.contact_persons || [],
                 secondary_address: r.data.secondary_address || {} });
    } catch (_e) { toast.error("Kunde nicht gefunden"); nav("/kunden"); }
  };

  const loadHistory = async () => {
    try { setHistory((await api.get(`/customers/${id}/history`)).data); }
    catch (_e) { setHistory(null); }
  };
  const loadPrices = async () => {
    try { setPrices((await api.get(`/customers/${id}/prices`)).data || []); }
    catch (_e) { setPrices([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get("/b2b-prices").then((r) => setB2bMap(Object.fromEntries((r.data || []).map((x) => [x.item_id, x])))).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "historie") loadHistory();
    if (tab === "preise") { loadPrices(); api.get("/items?page_size=200").then((r) => setItems(r.data || [])); }
    /* eslint-disable-next-line */
  }, [tab]);

  const saveDetails = async () => {
    try {
      const payload = { ...form, email: form.email || null };
      await api.patch(`/customers/${id}`, payload);
      toast.success("Stammdaten gespeichert"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const addContact = () => {
    if ((form.contact_persons || []).length >= 2) return;
    setForm({ ...form, contact_persons: [...(form.contact_persons || []), {}] });
  };
  const updateContact = (i, key, value) => {
    const list = [...(form.contact_persons || [])];
    list[i] = { ...list[i], [key]: value };
    setForm({ ...form, contact_persons: list });
  };
  const removeContact = (i) => {
    const list = [...(form.contact_persons || [])];
    list.splice(i, 1);
    setForm({ ...form, contact_persons: list });
  };

  if (!cust || !form) return <div className="p-6 text-[var(--text-muted)]">Lade Kunde…</div>;

  const tabs = [
    { key: "stammdaten", label: "Stammdaten", icon: User },
    { key: "kontakte", label: "Ansprechpartner", icon: Users, badge: (form.contact_persons || []).length },
    { key: "historie", label: "Kaufhistorie", icon: History },
    { key: "preise", label: "Kundenpreise", icon: Tag, hide: cust.type !== "B2B" },
    { key: "portal", label: "Portal-Zugang", icon: ShoppingBag },
  ].filter((t) => !t.hide);

  return (
    <div data-testid="page-customer-detail" className="pb-8">
      <PageHeader
        eyebrow={<Link to="/kunden" className="inline-flex items-center gap-1 hover:text-[var(--pop-pink)]">
          <ArrowLeft size={12} /> Zurück zur Kundenliste
        </Link>}
        title={cust.name}
        description={<div className="flex items-center gap-2">
          <TypeBadge type={cust.type} />
          {cust.customer_number && <span className="pop-pill bg-[var(--pop-cream)] text-xs font-mono">Kd-Nr. {cust.customer_number}</span>}
          {cust.city && <span className="text-sm text-[var(--text-muted)]">{cust.zip} {cust.city} · {cust.country}</span>}
        </div>}
      />

      {/* Tabs */}
      <div className="border-b border-[var(--border)] mb-6 flex items-center gap-1 overflow-x-auto">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 transition-colors ${
                      active ? "border-[var(--pop-pink)] text-[var(--pop-pink)]" : "border-transparent text-[var(--text-muted)] hover:text-black"
                    }`}>
              <Icon size={14} /> {t.label}
              {t.badge > 0 && <span className="pop-pill bg-[var(--pop-cream)] text-[10px] font-bold">{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {tab === "stammdaten" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="panel-stammdaten">
          <Card title="Firmen- & Steuerdaten">
            <Row label="Firmenname"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-detail-name" /></Row>
            <Row label="Kundennummer"><Input value={form.customer_number || ""} onChange={(e) => setForm({ ...form, customer_number: e.target.value })} /></Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Typ">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B</SelectItem>
                    <SelectItem value="B2C">B2C</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Zahlungsziel">
                <Select value={String(form.payment_terms_days ?? 14)}
                        onValueChange={(v) => setForm({ ...form, payment_terms_days: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Sofort</SelectItem>
                    <SelectItem value="14">14 Tage</SelectItem>
                    <SelectItem value="30">30 Tage</SelectItem>
                    <SelectItem value="60">60 Tage</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="USt-ID"><Input value={form.vat_id || ""} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} /></Row>
              <Row label="Steuernummer"><Input value={form.tax_number || ""} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="E-Mail"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Row>
              <Row label="E-Mail 2"><Input value={form.email2 || ""} onChange={(e) => setForm({ ...form, email2: e.target.value })} /></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Telefon"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Row>
              <Row label="Telefon 2"><Input value={form.phone2 || ""} onChange={(e) => setForm({ ...form, phone2: e.target.value })} /></Row>
            </div>
          </Card>

          <Card title={<><MapPin size={16} className="inline mr-1" /> Hauptadresse</>}>
            <Row label="Adresszusatz"><Input value={form.addr1_extra || ""} onChange={(e) => setForm({ ...form, addr1_extra: e.target.value })} /></Row>
            <Row label="Straße"><Input value={form.street || ""} onChange={(e) => setForm({ ...form, street: e.target.value })} /></Row>
            <div className="grid grid-cols-3 gap-3">
              <Row label="PLZ"><Input value={form.zip || ""} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></Row>
              <Row label="Ort"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Row>
              <Row label="Land"><Input value={form.country || "DE"} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Row>
            </div>
          </Card>

          <Card title={<><MapPin size={16} className="inline mr-1" /> Zweite Adresse (Lager/Filiale)</>}>
            <Row label="Adresszusatz 2"><Input value={form.secondary_address?.extra || ""}
                                                onChange={(e) => setForm({ ...form, secondary_address: { ...form.secondary_address, extra: e.target.value } })} /></Row>
            <Row label="Straße"><Input value={form.secondary_address?.street || ""}
                                        onChange={(e) => setForm({ ...form, secondary_address: { ...form.secondary_address, street: e.target.value } })} /></Row>
            <div className="grid grid-cols-3 gap-3">
              <Row label="PLZ"><Input value={form.secondary_address?.zip || ""}
                                       onChange={(e) => setForm({ ...form, secondary_address: { ...form.secondary_address, zip: e.target.value } })} /></Row>
              <Row label="Ort"><Input value={form.secondary_address?.city || ""}
                                       onChange={(e) => setForm({ ...form, secondary_address: { ...form.secondary_address, city: e.target.value } })} /></Row>
              <Row label="Land"><Input value={form.secondary_address?.country || ""}
                                        onChange={(e) => setForm({ ...form, secondary_address: { ...form.secondary_address, country: e.target.value } })} /></Row>
            </div>
          </Card>

          <Card title="Notizen">
            <textarea value={form.notes || ""}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       rows={8} data-testid="input-detail-notes"
                       className="w-full rounded-xl border border-[var(--border)] p-2 text-sm resize-none" />
          </Card>

          <div className="lg:col-span-2 flex justify-end">
            <Button onClick={saveDetails} data-testid="btn-save-detail"
                     className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              <Save size={14} className="mr-1" /> Speichern
            </Button>
          </div>
        </div>
      )}

      {tab === "kontakte" && (
        <div data-testid="panel-kontakte" className="space-y-4">
          {(form.contact_persons || []).map((cp, i) => (
            <div key={i} className="rounded-2xl border border-[var(--border)] bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-sm inline-flex items-center gap-1">
                  <User size={14} /> Ansprechpartner {i + 1}
                </div>
                <button onClick={() => removeContact(i)}
                         className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                  <Trash2 size={12} /> Entfernen
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Row label="Anrede"><Input value={cp.salutation || ""} onChange={(e) => updateContact(i, "salutation", e.target.value)} /></Row>
                <Row label="Vorname"><Input value={cp.first_name || ""} onChange={(e) => updateContact(i, "first_name", e.target.value)} /></Row>
                <Row label="Nachname"><Input value={cp.last_name || ""} onChange={(e) => updateContact(i, "last_name", e.target.value)} /></Row>
                <Row label="Position"><Input value={cp.name || ""} placeholder="z. B. Einkauf" onChange={(e) => updateContact(i, "name", e.target.value)} /></Row>
                <Row label="E-Mail"><Input type="email" value={cp.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)} /></Row>
                <Row label="Telefon"><Input value={cp.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)} /></Row>
              </div>
            </div>
          ))}
          {(form.contact_persons || []).length === 0 && (
            <div className="text-center py-8 text-[var(--text-muted)] text-sm">Noch keine Ansprechpartner hinterlegt.</div>
          )}
          <div className="flex justify-between items-center">
            {(form.contact_persons || []).length < 2 && (
              <Button variant="outline" onClick={addContact} data-testid="btn-add-contact"
                       className="rounded-full">
                <Plus size={14} className="mr-1" /> Ansprechpartner hinzufügen
              </Button>
            )}
            <Button onClick={saveDetails} data-testid="btn-save-contacts"
                     className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold ml-auto">
              <Save size={14} className="mr-1" /> Speichern
            </Button>
          </div>
        </div>
      )}

      {tab === "historie" && (
        <div data-testid="panel-historie">
          {!history ? (
            <div className="text-[var(--text-muted)] text-sm">Lade Historie…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <StatBox label="Belege" value={history.count} />
                <StatBox label="Netto gesamt" value={fmtEUR(history.total_net)} />
                <StatBox label="Brutto gesamt" value={fmtEUR(history.total_gross)} highlight />
              </div>
              <div className="rounded-2xl border border-[var(--border)] overflow-hidden">
                <Table className="table-zebra">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">Beleg</TableHead>
                      <TableHead className="overline">RE-Nr.</TableHead>
                      <TableHead className="overline">Datum</TableHead>
                      <TableHead className="overline">Status</TableHead>
                      <TableHead className="overline">Zahlung</TableHead>
                      <TableHead className="overline text-right">Positionen</TableHead>
                      <TableHead className="overline text-right">Brutto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell className="font-mono text-xs">{it.number || it.delivery_number || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{it.invoice_number || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{(it.delivery_date || it.shipped_at || it.created_at || "").slice(0, 10)}</TableCell>
                        <TableCell><StatusBadge status={it.status} /></TableCell>
                        <TableCell>{it.payment_status ? <StatusBadge status={it.payment_status} /> : "—"}</TableCell>
                        <TableCell className="text-right">{it.positions}</TableCell>
                        <TableCell className="text-right font-mono">{fmtEUR(it.gross)}</TableCell>
                      </TableRow>
                    ))}
                    {history.items.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[var(--text-muted)]">Noch keine Belege.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "preise" && (
        <div data-testid="panel-preise">
          <PricesPanel customerId={id} prices={prices} items={items} b2bMap={b2bMap}
                        reload={loadPrices} confirm={confirm} />
        </div>
      )}

      {tab === "portal" && (
        <div data-testid="panel-portal" className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 grid grid-cols-2 gap-4">
          <PortalAccessPanel customer={cust} />
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-white p-4">
      <div className="text-sm font-bold mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "bg-[var(--pop-pink)]/5 border-[var(--pop-pink)]/30" : "border-[var(--border)] bg-white"}`}>
      <div className="overline text-[var(--text-muted)]">{label}</div>
      <div className="font-display font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function PricesPanel({ customerId, prices, items, reload, confirm, b2bMap }) {
  const [form, setForm] = useState({ item_id: "", price: "", reason: "" });
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const takenIds = useMemo(() => new Set(prices.map((p) => p.item_id)), [prices]);

  const save = async () => {
    if (!form.item_id || !form.price) return toast.error("Artikel und Preis erforderlich");
    if (editing) {
      const ok = await confirm({
        title: "Preisänderung bestätigen?",
        description: `Der Kundenpreis für „${editing.item_name}" wird von ${editing.price?.toFixed(2)} € auf ${Number(form.price).toFixed(2)} € geändert. Die Änderung wird im Audit-Log protokolliert.`,
        confirmLabel: "Ja, Preis ändern",
      });
      if (!ok) return;
    }
    try {
      await api.post(`/customers/${customerId}/prices`, {
        item_id: form.item_id, price: Number(form.price), reason: form.reason || null,
      });
      toast.success(editing ? "Preis geändert" : "Preis hinterlegt");
      setForm({ item_id: "", price: "", reason: "" }); setEditing(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: "Kundenpreis entfernen?",
      description: `Der individuelle Preis für „${row.item_name}" wird gelöscht.`,
      confirmLabel: "Löschen", variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/customers/${customerId}/prices/${row.item_id}`); toast.success("Preis entfernt"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <>
      <div className="rounded-2xl border-2 border-[var(--pop-pink)]/30 bg-white p-4 mb-4">
        <div className="text-sm font-bold mb-2">
          {editing ? `Preis ändern für „${editing.item_name}"` : "Neuen Kundenpreis hinterlegen"}
        </div>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div className="col-span-2">
            <Label className="text-xs">Artikel</Label>
            <Select value={form.item_id} onValueChange={(v) => { const m = (b2bMap || {})[v]; setForm({ ...form, item_id: v, price: (m && m.price != null) ? String(m.price) : "" }); }} disabled={editing != null}>
              <SelectTrigger data-testid="select-detail-price-item"><SelectValue placeholder="Artikel auswählen…" /></SelectTrigger>
              <SelectContent>
                {items.filter((it) => (it.type === "finished") && (editing != null || !takenIds.has(it.id)))
                  .map((it) => <SelectItem key={it.id} value={it.id}>{it.name} · <span className="font-mono">{it.sku}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Preis (€)</Label>
            <Input type="number" step="0.01" min="0" value={form.price}
                    data-testid="input-detail-price-value"
                    onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
          <Button onClick={save} data-testid="btn-save-detail-price"
                   className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            {editing ? "Ändern" : "Hinterlegen"}
          </Button>
          <div className="col-span-4">
            <Label className="text-xs">Grund / Notiz (für Audit)</Label>
            <Input value={form.reason} placeholder="z. B. Rahmenvertrag Q1, Aktionspreis, …"
                    onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          {editing && (
            <div className="col-span-4 text-xs">
              <button onClick={() => { setEditing(null); setForm({ item_id: "", price: "", reason: "" }); }}
                       className="text-[var(--text-muted)] hover:underline">Bearbeiten abbrechen</button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--border)] overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Artikel</TableHead>
              <TableHead className="overline">SKU</TableHead>
              <TableHead className="overline text-right">Standard</TableHead>
              <TableHead className="overline text-right">Kundenpreis</TableHead>
              <TableHead className="overline">Zuletzt</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prices.map((p) => {
              const isOpen = expanded.has(p.id);
              const hist = (p.history || []).slice().reverse();
              return (
                <React.Fragment key={p.id}>
                  <TableRow data-testid={`detail-price-row-${p.item_sku}`}>
                    <TableCell className="font-semibold">
                      <button onClick={() => toggle(p.id)}
                              data-testid={`btn-toggle-detail-history-${p.item_sku}`}
                              className="mr-2 text-[var(--text-muted)] hover:text-[var(--pop-pink)] font-mono text-xs w-4 inline-block">
                        {isOpen ? "▾" : "▸"}
                      </button>
                      {p.item_name}
                      {hist.length > 0 && (
                        <span className="ml-2 pop-pill bg-[var(--pop-cream)] text-[10px]">
                          {hist.length} Änderung{hist.length === 1 ? "" : "en"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.item_sku}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-[var(--text-muted)]">
                      {p.item_default_price != null ? `${Number(p.item_default_price).toFixed(2)} €` : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-[var(--pop-pink)]">
                      {Number(p.price).toFixed(2)} €
                    </TableCell>
                    <TableCell className="text-xs text-[var(--text-muted)]">
                      {p.updated_at?.slice(0, 10)} · {p.updated_by || "—"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(p); setForm({ item_id: p.item_id, price: String(p.price), reason: "" }); }}
                               className="text-xs text-[var(--pop-pink)] hover:underline mr-3">
                        <Pencil size={12} className="inline mr-1" /> Ändern
                      </button>
                      <button onClick={() => remove(p)} className="text-xs text-red-600 hover:underline">
                        <Trash2 size={12} className="inline mr-1" /> Löschen
                      </button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-[var(--pop-cream)]/30">
                      <TableCell colSpan={6} className="p-3">
                        <div className="text-xs font-bold mb-2 inline-flex items-center gap-1 text-[var(--text-muted)]">
                          <History size={12} /> Audit-Log · {p.item_name}
                        </div>
                        {hist.length === 0 ? (
                          <div className="text-xs text-[var(--text-muted)] italic">Keine Historie vorhanden.</div>
                        ) : (
                          <div className="space-y-1" data-testid={`detail-history-${p.item_sku}`}>
                            {hist.map((h, i) => (
                              <div key={i} className="grid grid-cols-4 gap-2 items-center text-xs bg-white rounded-lg p-2 border">
                                <div className="font-mono font-bold text-[var(--pop-pink)]">
                                  {h.price != null ? `${Number(h.price).toFixed(2)} €` : "—"}
                                </div>
                                <div className="text-[var(--text-muted)]">
                                  {h.changed_at ? new Date(h.changed_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                                </div>
                                <div className="font-semibold">{h.changed_by || "—"}</div>
                                <div className="italic text-[var(--text-muted)]">{h.reason || "—"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {prices.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--text-muted)]">
                Noch keine Kundenpreise hinterlegt.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
// eslint-disable-next-line no-unused-vars
const _ = React;
